import { useState } from 'react';

import { getApiV1PersonIdAttachmentsAttachmentidDownload } from '@/shared/api/generated';

/**
 * Hook para fazer download de um anexo de pessoa.
 *
 * Chama `GET /api/v1/person/{id}/attachments/{attachmentId}/download` com
 * `responseType: 'blob'` e aciona o download nativo do navegador.
 *
 * @param personId - ID da pessoa dona do anexo.
 * @returns `{ download, downloadingId }` — função de download e ID do anexo em andamento.
 *
 * @example
 * ```tsx
 * const { download, downloadingId } = useDownloadPersonAttachment(person.id);
 * <IconButton
 *   isLoading={downloadingId === attachment.id}
 *   onClick={() => download(attachment.id, attachment.fileName)}
 * />
 * ```
 */
export function useDownloadPersonAttachment(personId: number) {
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const download = async (attachmentId: number, fileName: string | null | undefined) => {
    setDownloadingId(attachmentId);
    try {
      const data = await getApiV1PersonIdAttachmentsAttachmentidDownload(personId, attachmentId, {
        responseType: 'blob',
      });
      const blob = data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName ?? 'documento';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  return { download, downloadingId } as const;
}
